/**
 * The Investor Portals overview's Portals tab (#15170, match-mockup spec
 * section 1): coverage as the client tracks it, under "Covering N managers
 * across M portals" and the muted "Checked every business day · k documents
 * in the last 7 days".
 *
 * - By manager: one flat row per manager and portal pair, no group heading
 *   rows: Manager, Portal (its logo and name, a link to its page), Last
 *   checked, Last 7 days, Newest document. The row opens the manager's page;
 *   a portal matched to no manager is one row at the end, Manager "—", that
 *   opens the portal's page.
 * - By portal: one row per portal: Portal, Managers (links to each manager's
 *   page), Last checked, Last 7 days, Newest document. The row opens the
 *   portal's page.
 *
 * Only client-healthy portals reach this component (the page filters them),
 * and it says nothing about a portal's state: no status column, no pill, no
 * failure wording. The rules are in `browser/portals.ts`; this file only
 * draws them.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  PortalNewestDocument,
  PortalService,
} from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import { ServiceFavicon } from "@/components/common/ServiceFavicon";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import { Segmented } from "@/components/FpPages/RMS/design";
import { createEntitySlug } from "@/components/FpPages/RMS/Dashboard/utils/slugUtils";
import { GlobeIcon } from "@phosphor-icons/react";
import { RowPlaceholders, Section } from "./HomeSections";
import { rowActivationProps } from "./rowActivation";
import CutText from "./browser/CutText";
import {
  arrivalDayLabel,
  checkedLabel,
  coverageHeading,
  coverageRows,
  coverageSubtitle,
  portalRows,
  type CoverageManager,
} from "./browser/portals";

export interface PortalsOverviewProps {
  services: PortalService[];
  /** A portal's page, by the service's name. */
  onPickService: (service: string) => void;
  /** Inside a service the page's title block describes it, so the band
   *  shows nothing for it once it is resolved. */
  inService?: boolean;
  /** The service the page is inside, as the page resolved it; null while
   *  the services load, or when they could not be read. */
  service?: PortalService | null;
  /** The services read failed with no answer cached. */
  error?: boolean;
  /** The services read is still out: placeholders, no heading counts. */
  loading?: boolean;
  /** The host's frame is too narrow for the table: stacked blocks. */
  compact?: boolean;
}

/** What a logo needs of a portal: its name and its icon host. */
export type PortalMarkOf = Pick<PortalService, "service" | "icon_host">;

/** A service's favicon, the globe when it has none: every place a portal
 *  is named (20px in tables, 16px in document rows, 30px beside the portal
 *  page's title). */
export const Mark: React.FC<{
  service: PortalMarkOf;
  size?: number;
}> = ({ service, size = 16 }) => (
  <ServiceFavicon
    host={service.icon_host}
    label={service.service}
    size={size}
    fallback={
      <GlobeIcon
        size={size}
        aria-hidden
        className="shrink-0 text-[var(--fp-ink-3)]"
      />
    }
  />
);

/** A manager's RMS record page (match-mockup spec: "opens the manager
 *  page"). */
export const managerPageHref = (manager: CoverageManager): string =>
  `/rms/manager/${createEntitySlug({ id: manager.publicId, name: manager.name })}`;

type Grouping = "manager" | "portal";

const GROUPINGS: { key: Grouping; label: string }[] = [
  { key: "manager", label: "By manager" },
  { key: "portal", label: "By portal" },
];

export const ROW_INTERACTION =
  "cursor-pointer outline-none hover:bg-[var(--fp-hover-row)] focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-[var(--fp-ink-3)]";

export const listTh =
  "whitespace-nowrap px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-[var(--fp-ink-3)] border-b border-[var(--fp-border)]";
export const listTd =
  "h-[var(--fp-row-h)] overflow-hidden text-ellipsis whitespace-nowrap px-3 py-0 align-middle text-[13px] text-[var(--fp-ink)]";
export const numTd = "[font-variant-numeric:tabular-nums]";

/** A link inside a row that opens something else than the row: its own
 *  click and keys stop at it. */
export const InlineLink: React.FC<{
  onOpen: () => void;
  children: React.ReactNode;
  className?: string;
}> = ({ onOpen, children, className = "" }) => (
  <button
    type="button"
    onClick={(event) => {
      event.stopPropagation();
      onOpen();
    }}
    onKeyDown={(event) => event.stopPropagation()}
    className={`min-w-0 truncate text-left text-[13px] text-[var(--fp-accent)] hover:underline ${className}`}
  >
    {children}
  </button>
);

/** A portal's logo and name; a link to its page when `onOpen` is given. */
export const PortalName: React.FC<{
  service: PortalService;
  size?: number;
  onOpen?: () => void;
}> = ({ service, size = 20, onOpen }) => (
  <span className="flex min-w-0 items-center gap-2">
    <span className="flex shrink-0" data-testid="portal-logo">
      <Mark service={service} size={size} />
    </span>
    {onOpen ? (
      <InlineLink onOpen={onOpen}>{service.service}</InlineLink>
    ) : (
      <CutText
        text={service.service}
        className="min-w-0 truncate text-[13px] text-[var(--fp-ink)]"
      />
    )}
  </span>
);

/** "<document name> · <day>", or the muted "Nothing new this week": the
 *  read's own newest of the week, the rule its Last 7 days counts by. */
const NewestDocument: React.FC<{
  newest: PortalNewestDocument | null;
  now: Date;
}> = ({ newest, now }) => {
  const zone = useViewerTimeZone();
  if (newest === null)
    return (
      <span className="text-[var(--fp-ink-3)]">Nothing new this week</span>
    );
  const at = newest.uploaded_at;
  const day =
    at.granularity === "instant" ? arrivalDayLabel(at.at, zone, now) : at.day;
  // The name gives way to the day: a long name is cut, the day never is.
  return (
    <span className="flex min-w-0 items-baseline gap-1">
      <span data-testid="newest-name" className="min-w-0 truncate">
        {newest.name}
      </span>{" "}
      <span
        data-testid="newest-day"
        className="shrink-0 text-[var(--fp-ink-3)]"
      >
        · {day}
      </span>
    </span>
  );
};

/** The managers a portal delivers for, each a link to its page. */
export const ManagerLinks: React.FC<{
  managers: CoverageManager[];
  onOpenManager: (manager: CoverageManager) => void;
}> = ({ managers, onOpenManager }) =>
  managers.length === 0 ? (
    <span className="text-[var(--fp-ink-3)]">—</span>
  ) : (
    <span className="min-w-0 truncate">
      {managers.map((manager, index) => (
        <React.Fragment key={manager.publicId}>
          {index > 0 && ", "}
          <InlineLink onOpen={() => onOpenManager(manager)}>
            {manager.name}
          </InlineLink>
        </React.Fragment>
      ))}
    </span>
  );

const ByManagerTable: React.FC<{
  services: PortalService[];
  now: Date;
  onPickService: (service: string) => void;
  onOpenManager: (manager: CoverageManager) => void;
}> = ({ services, now, onPickService, onOpenManager }) => {
  const zone = useViewerTimeZone();
  const rows = useMemo(() => coverageRows(services), [services]);
  return (
    <table className="w-full table-fixed border-collapse">
      <thead>
        <tr>
          <th scope="col" className={`${listTh} w-[20%]`}>
            Manager
          </th>
          <th scope="col" className={`${listTh} w-[22%]`}>
            Portal
          </th>
          <th scope="col" className={`${listTh} w-[15%]`}>
            Last checked
          </th>
          <th scope="col" className={`${listTh} w-[10%] text-right`}>
            Last 7 days
          </th>
          <th scope="col" className={listTh}>
            Newest document
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const manager = row.manager;
          return (
            <tr
              key={row.key}
              data-service={row.service.service}
              data-manager={manager?.name ?? ""}
              className={`border-b border-[var(--fp-border)] ${ROW_INTERACTION}`}
              {...rowActivationProps(() =>
                manager === null
                  ? onPickService(row.service.service)
                  : onOpenManager(manager)
              )}
            >
              <td className={`${listTd} font-semibold`}>
                {manager === null ? (
                  <span className="font-normal text-[var(--fp-ink-3)]">—</span>
                ) : (
                  <span className="flex min-w-0">
                    <CutText text={manager.name} className="min-w-0 truncate" />
                  </span>
                )}
              </td>
              <td className={listTd}>
                <PortalName
                  service={row.service}
                  onOpen={() => onPickService(row.service.service)}
                />
              </td>
              <td className={`${listTd} ${numTd} text-[var(--fp-ink-2)]`}>
                {checkedLabel(row.service.last_collected_at, zone, now)}
              </td>
              <td className={`${listTd} ${numTd} text-right`}>
                {row.weekCount.toLocaleString()}
              </td>
              <td className={listTd}>
                <NewestDocument newest={row.newest} now={now} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const ByPortalTable: React.FC<{
  services: PortalService[];
  now: Date;
  onPickService: (service: string) => void;
  onOpenManager: (manager: CoverageManager) => void;
}> = ({ services, now, onPickService, onOpenManager }) => {
  const zone = useViewerTimeZone();
  const rows = useMemo(() => portalRows(services), [services]);
  return (
    <table className="w-full table-fixed border-collapse">
      <thead>
        <tr>
          <th scope="col" className={`${listTh} w-[22%]`}>
            Portal
          </th>
          <th scope="col" className={`${listTh} w-[22%]`}>
            Managers
          </th>
          <th scope="col" className={`${listTh} w-[15%]`}>
            Last checked
          </th>
          <th scope="col" className={`${listTh} w-[10%] text-right`}>
            Last 7 days
          </th>
          <th scope="col" className={listTh}>
            Newest document
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.key}
            data-service={row.service.service}
            className={`border-b border-[var(--fp-border)] ${ROW_INTERACTION}`}
            {...rowActivationProps(() => onPickService(row.service.service))}
          >
            <td className={`${listTd} font-medium`}>
              <PortalName service={row.service} />
            </td>
            <td className={listTd}>
              <ManagerLinks
                managers={row.managers}
                onOpenManager={onOpenManager}
              />
            </td>
            <td className={`${listTd} ${numTd} text-[var(--fp-ink-2)]`}>
              {checkedLabel(row.service.last_collected_at, zone, now)}
            </td>
            <td className={`${listTd} ${numTd} text-right`}>
              {row.weekCount.toLocaleString()}
            </td>
            <td className={listTd}>
              <NewestDocument newest={row.newest} now={now} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/** The phone's rows: the same flat rows, stacked, no group headings. */
const CoverageBlocks: React.FC<{
  services: PortalService[];
  grouping: Grouping;
  now: Date;
  onPickService: (service: string) => void;
  onOpenManager: (manager: CoverageManager) => void;
}> = ({ services, grouping, now, onPickService, onOpenManager }) => {
  const zone = useViewerTimeZone();
  const blocks =
    grouping === "manager"
      ? coverageRows(services).map((row) => ({
          key: row.key,
          service: row.service,
          title: row.manager?.name ?? "—",
          weekCount: row.weekCount,
          open: () =>
            row.manager === null
              ? onPickService(row.service.service)
              : onOpenManager(row.manager),
        }))
      : portalRows(services).map((row) => ({
          key: row.key,
          service: row.service,
          title: row.managers.map((m) => m.name).join(", ") || "—",
          weekCount: row.weekCount,
          open: () => onPickService(row.service.service),
        }));
  return (
    <ul className="m-0 list-none border-t border-[var(--fp-border)] p-0">
      {blocks.map((block) => (
        <li
          key={block.key}
          data-service={block.service.service}
          className={`flex flex-col gap-1 border-b border-[var(--fp-border)] px-4 py-2.5 ${ROW_INTERACTION}`}
          {...rowActivationProps(block.open)}
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="min-w-0 flex-1">
              <PortalName
                service={block.service}
                onOpen={() => onPickService(block.service.service)}
              />
            </span>
            <span className="min-w-0 max-w-[50%] truncate text-[13px] font-semibold text-[var(--fp-ink)]">
              {block.title}
            </span>
          </span>
          <span className="text-[12px] text-[var(--fp-ink-3)] [font-variant-numeric:tabular-nums]">
            {checkedLabel(block.service.last_collected_at, zone, now)}
            {" · "}
            {block.weekCount.toLocaleString()} in the last 7 days
          </span>
        </li>
      ))}
    </ul>
  );
};

const PortalsOverview: React.FC<PortalsOverviewProps> = ({
  services,
  onPickService,
  loading = false,
  inService = false,
  service = null,
  error = false,
  compact = false,
}) => {
  const phone = useIsPhoneViewport();
  const navigate = useNavigate();
  const [grouping, setGrouping] = useState<Grouping>("manager");
  // One clock read per render of the tab: every row's day labels agree.
  const now = useMemo(() => new Date(), []);
  const onOpenManager = (manager: CoverageManager) =>
    navigate(managerPageHref(manager));

  // A resolved portal is described in the page's own sections. Inside one
  // the read is still out (a failed read shows the overview instead).
  if (inService && service !== null) return null;
  if (inService) {
    return (
      <section aria-label="Portal" aria-busy={loading || undefined}>
        {loading && <RowPlaceholders />}
      </section>
    );
  }
  if (error) {
    return (
      <Section title="Portals">
        <p className="m-0 px-4 text-[13px] text-[var(--fp-ink-3)]">
          Could not load the portals
        </p>
      </Section>
    );
  }
  if (loading) {
    return (
      <Section title="Portals">
        <RowPlaceholders />
      </Section>
    );
  }
  if (services.length === 0) {
    return (
      <Section title="Portals">
        <p className="m-0 px-4 text-[13px] text-[var(--fp-ink-3)]">
          No investor portals yet
        </p>
      </Section>
    );
  }
  const props = { services, now, onPickService, onOpenManager };
  return (
    <Section
      title={coverageHeading(services)}
      trailing={
        <span
          data-testid="coverage-subtitle"
          className="text-[12.5px] font-normal text-[var(--fp-ink-3)]"
        >
          {coverageSubtitle(services)}
        </span>
      }
      end={
        <Segmented
          aria-label="Group by"
          size="compact"
          options={GROUPINGS}
          value={grouping}
          onChange={setGrouping}
        />
      }
    >
      {phone || compact ? (
        <CoverageBlocks {...props} grouping={grouping} />
      ) : (
        <div className="px-4">
          {grouping === "manager" ? (
            <ByManagerTable {...props} />
          ) : (
            <ByPortalTable {...props} />
          )}
        </div>
      )}
    </Section>
  );
};

export default PortalsOverview;
